import { z } from "zod";

/**
 * Articles: what the writer may return, and what code makes of it
 * ([ADR 0033](../../../docs/adr/0033-articles-are-written-from-sources-approved-by-a-person-and-published-on-the-customers-own-site.md),
 * [21](../../../docs/21-articles-and-seo.md)).
 *
 * The words are the product, and a wrong fact is published under the customer's name on a page search engines keep.
 * So the model proposes and this file decides, the same way the Business page (ADR 0027) and the Studio brief
 * (ADR 0031) are decided: `applyArticle` is pure, free, and run again after every edit. Nothing reaches a site that
 * did not come through it.
 *
 * What it can do it does absolutely — a citation to a source that was not given is dropped, a competitor's name
 * cannot be approved past, a link to somewhere nobody cited is removed, the JSON-LD is built here and never written
 * by a model. What it cannot do it flags for a person: it cannot tell an honest sentence from a hint at a rival, and
 * it cannot know that a sourced number means what the sentence says it means.
 */

/** Where a sentence's facts came from. `S1`… is the customer's own source material; `B:what` is a Business page section. */
export const SourceRef = z.string().regex(/^(S[1-9][0-9]{0,3}|B:[a-z]+)$/);
export type SourceRef = z.infer<typeof SourceRef>;

/** One numbered thing the writer was given to write from, and nothing else was. */
export const ArticleSource = z.object({
  ref: SourceRef,
  title: z.string().min(1).max(200),
  /** The text as it was read, which is what the writer saw — never the page as it is today. */
  text: z.string().max(40_000),
  /** Where it came from, when it came from somewhere. The only external addresses an article may link to. */
  url: z.string().default(""),
});
export type ArticleSource = z.infer<typeof ArticleSource>;

/** A topic, before anything is written. Free to accept, edit and drop; writing is what costs. */
export const ArticleIntent = z.enum(["informational", "commercial", "transactional", "navigational"]);
export type ArticleIntent = z.infer<typeof ArticleIntent>;

export const TopicProposal = z.object({
  targetPhrase: z.string().min(2).max(120),
  audience: z.string().min(2).max(200),
  intent: ArticleIntent,
  angle: z.string().min(2).max(300),
  /** Which of the given sources it would lean on, by ref. Unknown refs are dropped. */
  sourceRefs: z.array(SourceRef).max(20).default([]),
});
export type TopicProposal = z.infer<typeof TopicProposal>;

/** What `plan_topics` returns. Up to ten; code drops what it may not propose. */
export const TopicPlan = z.object({ topics: z.array(TopicProposal).max(10) });
export type TopicPlan = z.infer<typeof TopicPlan>;

/** One H2 and the Markdown under it. The writer never writes an H1: the page's title is the H1. */
export const DraftSection = z.object({ heading: z.string().min(1).max(120), markdown: z.string().min(1).max(20_000) });
export type DraftSection = z.infer<typeof DraftSection>;

export const DraftQuestion = z.object({ question: z.string().min(1).max(200), answer: z.string().min(1).max(2_000) });
export type DraftQuestion = z.infer<typeof DraftQuestion>;

/**
 * What `write_article` and `translate_article` return. Read leniently and trusted for nothing: every field below is
 * a proposal, and `applyArticle` decides what survives.
 */
export const ArticleDraft = z.object({
  title: z.string().min(1).max(200),
  metaDescription: z.string().min(1).max(400),
  slug: z.string().min(1).max(200),
  sections: z.array(DraftSection).min(1).max(12),
  faq: z.array(DraftQuestion).max(8).default([]),
});
export type ArticleDraft = z.infer<typeof ArticleDraft>;

/** What code found, and what a person did about it. Approve is impossible while one is unresolved. */
export const FlagKind = z.enum([
  /** `[?]`, or a sentence carrying a figure and citing nobody. */
  "no_source",
  /** A figure in a cited sentence that none of the sources it cites contains. */
  "not_in_source",
  /** A citation to a source the writer was not given. Dropped; the sentence is left to be resolved. */
  "unknown_source",
  /** A name on the never-name list, or one of the businesses the lookup found. Removal is the only way out. */
  "never_name",
]);
export type FlagKind = z.infer<typeof FlagKind>;

export const FlagResolution = z.enum(["open", "removed", "cited", "stands"]);
export type FlagResolution = z.infer<typeof FlagResolution>;

export const ArticleFlag = z.object({
  /** Stable within a version, so a resolution recorded against it still means the same sentence. */
  id: z.string().min(1),
  kind: FlagKind,
  /** Where it is: `s{index}` for a section, `q{index}` for an FAQ answer, `title`, `meta`. */
  at: z.string().min(1),
  /** The sentence itself, as the person will read it above the article. */
  sentence: z.string(),
  /** What set it off: the figure, or the name. */
  detail: z.string().default(""),
  resolution: FlagResolution.default("open"),
  /** Who said "I stand behind this", and when. The person becomes the source, and that is recorded. */
  resolvedBy: z.string().default(""),
  resolvedAt: z.string().default(""),
});
export type ArticleFlag = z.infer<typeof ArticleFlag>;

/** One cited sentence and what it cites, for the Sources toggle beside the text. */
export const ArticleClaim = z.object({ at: z.string(), sentence: z.string(), refs: z.array(SourceRef) });
export type ArticleClaim = z.infer<typeof ArticleClaim>;

export const ArticleLink = z.object({ href: z.string(), text: z.string(), kind: z.enum(["offer", "internal", "external"]) });
export type ArticleLink = z.infer<typeof ArticleLink>;

/** A version, as `applyArticle` made it. Immutable once written ([01](../../../docs/01-domain-model.md) invariant 4). */
export interface AppliedArticle {
  title: string;
  metaDescription: string;
  slug: string;
  /** The body as it will be published: no H1, no citation markers, the FAQ kept out of it. */
  bodyMd: string;
  /** The body with `[S2]` still in it, which is what the review screen underlines. */
  reviewMd: string;
  faq: DraftQuestion[];
  claims: ArticleClaim[];
  links: ArticleLink[];
  flags: ArticleFlag[];
  jsonLd: Record<string, unknown>[];
}

/** Everything `applyArticle` needs to decide. All of it is the site's and the customer's; none of it is the model's. */
export interface ApplyContext {
  sources: ArticleSource[];
  /** Names that may never appear: the customer's own list, plus every business the lookup found (`rivals`). */
  neverName: string[];
  /** The sign-up or offer page for this language, and the words its link reads. */
  offer: { path: string; label: string };
  /** Paths of this site's own published articles: the only internal links other than the offer page. */
  published: string[];
  /** Slugs already used on this site in this language. A collision gets `-2`. */
  takenSlugs: string[];
  site: {
    baseUrl: string;
    blogPath: string;
    trailingSlash: boolean;
    /** This edition's language and its path prefix (`""` for the default language, `/fr` otherwise). */
    lang: string;
    prefix: string;
    businessName: string;
  };
  publishedAt: string;
  modifiedAt: string;
}

const FIGURE = /(?:[£$€]\s?\d[\d.,]*|\d[\d.,]*\s?%|\b\d{1,4}(?:[.,]\d+)?\b)/g;
const MONTHS = /\b(january|february|march|april|may|june|july|august|september|october|november|december)\b/gi;
const CITATION = /\[(S[1-9][0-9]{0,3}|B:[a-z]+|\?)\]/g;
const MD_LINK = /\[([^\]]*)\]\((?!S[1-9]|B:|\?)([^)\s]+)(?:\s+"[^"]*")?\)/g;

/** Digits as they can be compared: commas, spaces and currency dropped, so "1,200" in a sentence finds "1200" in a source. */
const flatten = (s: string) => s.toLowerCase().replace(/[\s,£$€]/g, "");

/**
 * Sentences, roughly. Roughly is the right amount: this splits so a person can be shown the claim that was flagged,
 * and a sentence that comes out a clause too long is still the sentence they recognise. Abbreviations are left alone
 * rather than listed, because a list of them is a list nobody maintains.
 */
export function sentences(markdown: string): string[] {
  return markdown
    .split(/\n{2,}/)
    .flatMap((para) => para.split(/(?<=[.!?])\s+(?=[A-Z£$€"'(\[])/))
    .map((s) => s.trim())
    .filter(Boolean);
}

/** The figures in a sentence, as a person would name them: amounts, percentages, plain numbers, month names. */
export function figuresIn(sentence: string): string[] {
  const plain = sentence.replace(CITATION, " ").replace(MD_LINK, "$1");
  return [...new Set([...(plain.match(FIGURE) ?? []), ...(plain.match(MONTHS) ?? [])].map((f) => f.trim()))];
}

/** A slug as it can go in an address: lower-case ASCII, hyphens, sixty characters, and never one this site already has. */
export function articleSlug(raw: string, taken: string[] = []): string {
  const base = raw.normalize("NFKD").replace(/\p{M}/gu, "").toLowerCase()
    .replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 60).replace(/-+$/, "") || "article";
  if (!taken.includes(base)) return base;
  for (let n = 2; n < 1000; n++) {
    const next = `${base.slice(0, 57).replace(/-+$/, "")}-${n}`;
    if (!taken.includes(next)) return next;
  }
  return base;
}

/** Where an edition lives on the customer's site: `/fr` + `/blog` + `/` + slug, with or without the trailing slash. */
export function articlePath(site: Pick<ApplyContext["site"], "blogPath" | "prefix" | "trailingSlash">, slug: string): string {
  const path = `${site.prefix}${site.blogPath}/${slug}`.replace(/\/{2,}/g, "/");
  return site.trailingSlash ? `${path}/` : path;
}

export const articleUrl = (site: ApplyContext["site"], slug: string) =>
  `${site.baseUrl.replace(/\/+$/, "")}${articlePath(site, slug)}`;

/**
 * A name is named when it appears as a word, so "Parcel2Go" is found and "parcel" in "parcelled" is not. Exported
 * for the posts a business approves, which keep the same list ([31](../../../docs/31-posts-joogoos-drafts.md)).
 */
export function namesIn(text: string, neverName: string[]): string[] {
  const lower = text.toLowerCase();
  return neverName.filter((n) => n.trim().length > 1).filter((n) => {
    const at = lower.indexOf(n.toLowerCase());
    if (at < 0) return false;
    const before = lower[at - 1] ?? " ";
    const after = lower[at + n.length] ?? " ";
    return !/[a-z0-9]/.test(before) && !/[a-z0-9]/.test(after);
  });
}

/**
 * The one function that decides what an article is.
 *
 * Run on every draft, every translation and after every edit a person makes, so the flags on screen are always about
 * the text on screen. It never throws and never asks a model anything: given a draft and the customer's own settings
 * it returns the version, the flags standing in the way of Approve, and the structured data.
 */
export function applyArticle(draft: ArticleDraft, ctx: ApplyContext): AppliedArticle {
  const known = new Map(ctx.sources.map((s) => [s.ref, s]));
  const flags: ArticleFlag[] = [];
  const claims: ArticleClaim[] = [];
  const links: ArticleLink[] = [];
  let seq = 0;
  const flag = (kind: FlagKind, at: string, sentence: string, detail: string) => {
    flags.push({ id: `f${++seq}`, kind, at, sentence: sentence.slice(0, 600), detail, resolution: "open", resolvedBy: "", resolvedAt: "" });
  };

  /** One piece of prose, checked. Returns it with unknown citations and disallowed links taken out. */
  const check = (at: string, text: string): string => {
    let out = text;

    // A citation to a source the writer was not given is not a weaker citation; it is a made-up one. It goes, and
    // the sentence it was propping up is left flagged as the unsourced sentence it actually is.
    const invented = new Set<string>();
    out = out.replace(CITATION, (whole, ref: string) => {
      if (ref === "?" || known.has(ref)) return whole;
      invented.add(ref);
      return "";
    });

    // Links. Internal ones may only be the offer page or one of this site's published articles; external ones may
    // only be an address a source this article cites came from. Everything else is removed, keeping its words: a
    // model that invents a URL has invented a destination, not a sentence.
    const allowedHosts = new Set(ctx.sources.map((s) => hostOf(s.url)).filter(Boolean));
    out = out.replace(MD_LINK, (whole, label: string, href: string) => {
      if (href.startsWith("/")) {
        const bare = href.replace(/\/+$/, "") || "/";
        if (bare === ctx.offer.path.replace(/\/+$/, "")) { links.push({ href, text: label, kind: "offer" }); return whole; }
        if (ctx.published.some((p) => p.replace(/\/+$/, "") === bare)) { links.push({ href, text: label, kind: "internal" }); return whole; }
        return label;
      }
      if (/^https?:\/\//i.test(href) && allowedHosts.has(hostOf(href))) {
        links.push({ href, text: label, kind: "external" }); return whole;
      }
      return label;
    });

    for (const sentence of sentences(out)) {
      const refs = [...sentence.matchAll(CITATION)].map((m) => m[1]!);
      const cited = refs.filter((r): r is SourceRef => r !== "?");
      const unsure = refs.includes("?");
      const figures = figuresIn(sentence);

      for (const name of namesIn(sentence, ctx.neverName)) flag("never_name", at, sentence, name);
      if (invented.size && cited.length === 0 && (unsure || figures.length)) {
        flag("unknown_source", at, sentence, [...invented].join(", "));
      } else if (unsure || (figures.length && cited.length === 0)) {
        flag("no_source", at, sentence, figures.join(", "));
      } else if (cited.length) {
        claims.push({ at, sentence: strip(sentence), refs: cited });
        const haystack = cited.map((r) => flatten(known.get(r)?.text ?? "")).join(" ");
        const missing = figures.filter((f) => !haystack.includes(flatten(f)));
        if (missing.length) flag("not_in_source", at, sentence, missing.join(", "));
      }
    }
    return out;
  };

  const title = check("title", draft.title).slice(0, 60).trim();
  const metaDescription = strip(check("meta", draft.metaDescription)).slice(0, 155).trim();
  const sections = draft.sections.map((s, i) => ({ heading: strip(s.heading).slice(0, 120), markdown: check(`s${i}`, s.markdown) }));
  const faq = draft.faq.map((q, i) => ({ question: strip(q.question), answer: check(`q${i}`, q.answer) }));

  // Every article ends somewhere. If the writer did not link the offer page, one is added in the words the customer
  // set beside it in the site settings — not in words a model chose, because that sentence is the customer's promise.
  if (!links.some((l) => l.kind === "offer") && sections.length) {
    const last = sections[sections.length - 1]!;
    last.markdown = `${last.markdown.trimEnd()}\n\n[${ctx.offer.label}](${ctx.offer.path})`;
    links.push({ href: ctx.offer.path, text: ctx.offer.label, kind: "offer" });
  }

  const slug = articleSlug(draft.slug || title, ctx.takenSlugs);
  const reviewMd = sections.map((s) => `## ${s.heading}\n\n${s.markdown.trim()}`).join("\n\n");
  const bodyMd = strip(reviewMd);

  return {
    title, metaDescription, slug, bodyMd, reviewMd,
    faq: faq.map((q) => ({ question: q.question, answer: strip(q.answer).trim() })),
    claims, links, flags,
    jsonLd: articleJsonLd({ title, metaDescription, slug, faq: faq.map((q) => ({ question: q.question, answer: strip(q.answer).trim() })) }, ctx),
  };
}

/** Citation markers are review furniture. They are what a person reads the sources by, and they never reach a site. */
export const strip = (text: string) => text.replace(CITATION, "").replace(/[ \t]+([.,;:!?])/g, "$1").replace(/[ \t]{2,}/g, " ").replace(/[ \t]+$/gm, "");

const hostOf = (url: string) => { try { return new URL(url).host.toLowerCase(); } catch { return ""; } };

/**
 * The structured data, built from the version. The model never writes JSON-LD: it is the one part of a page that is
 * addressed to a machine, so a hallucinated field in it is a lie nobody reads before it is believed.
 */
export function articleJsonLd(
  v: { title: string; metaDescription: string; slug: string; faq: DraftQuestion[] },
  ctx: ApplyContext,
): Record<string, unknown>[] {
  const url = articleUrl(ctx.site, v.slug);
  const org = { "@type": "Organization", name: ctx.site.businessName, url: ctx.site.baseUrl.replace(/\/+$/, "") };
  const out: Record<string, unknown>[] = [{
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: v.title,
    description: v.metaDescription,
    inLanguage: ctx.site.lang,
    datePublished: ctx.publishedAt,
    dateModified: ctx.modifiedAt,
    mainEntityOfPage: url,
    author: org,
    publisher: org,
  }];
  if (v.faq.length) {
    out.push({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: v.faq.map((q) => ({ "@type": "Question", name: q.question, acceptedAnswer: { "@type": "Answer", text: q.answer } })),
    });
  }
  out.push({
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Blog", item: `${ctx.site.baseUrl.replace(/\/+$/, "")}${ctx.site.prefix}${ctx.site.blogPath}` },
      { "@type": "ListItem", position: 2, name: v.title, item: url },
    ],
  });
  return out;
}

/** Nothing is approved while a flag is open, and a `never_name` flag is only ever resolved by removing the name. */
export const openFlags = (flags: ArticleFlag[]) => flags.filter((f) => f.resolution === "open");
export const mayApprove = (flags: ArticleFlag[]) => openFlags(flags).length === 0;
